import { useMutation } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { useId, useRef, useState } from "react";

import {
  useReprocessBulkUpload,
  useRequestBulkDocumentUploadUrls,
} from "@elation/api-client/src/stitch-web";
import type { BulkUploadUrlsRequestFilesItemMimeType } from "@elation/api-client/src/stitch-web/models";

import { Text } from "@src/shared/components/general/Text";
import {
  Flexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { cn } from "@src/shared/lib/utils";

import { BulkJobCard } from "./BulkJobCard";
import type { BulkDocumentJob } from "./useBulkDocumentJobs";

const MAX_FILES_PER_BATCH = 100;
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB
const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
]);

interface BulkImportPanelProps {
  jobs: BulkDocumentJob[];
  onJobsAdded: (jobs: BulkDocumentJob[]) => void;
  onJobFailed: (jobId: string) => void;
  onJobRemove: (jobId: string) => void;
}

export const BulkImportPanel = ({
  jobs,
  onJobsAdded,
  onJobFailed,
  onJobRemove,
}: BulkImportPanelProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTriggerId = useId();

  const { mutateAsync: requestUrls } = useRequestBulkDocumentUploadUrls();
  const { mutate: reprocessBulk } = useReprocessBulkUpload();

  const { mutateAsync: uploadToS3 } = useMutation({
    mutationFn: async ({ url, file }: { url: string; file: File }) => {
      const response = await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!response.ok) {
        throw new Error(`S3 upload failed with status ${response.status}`);
      }
    },
  });

  const handleFiles = async (files: FileList | null) => {
    setError(null);
    if (!files?.length) return;

    const fileArray = Array.from(files);

    if (fileArray.length > MAX_FILES_PER_BATCH) {
      setError(
        `Too many files: ${fileArray.length} of ${MAX_FILES_PER_BATCH} max per batch.`,
      );
      return;
    }

    const invalidMime = fileArray.find((f) => !ALLOWED_MIME_TYPES.has(f.type));
    if (invalidMime) {
      setError(`Unsupported file type: ${invalidMime.name}`);
      return;
    }

    const oversized = fileArray.find((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized) {
      setError(`File too large: ${oversized.name} (max 25 MB per file).`);
      return;
    }

    try {
      const { data } = await requestUrls({
        data: {
          files: fileArray.map((f) => ({
            fileName: f.name,
            mimeType: f.type as BulkUploadUrlsRequestFilesItemMimeType,
          })),
        },
      });

      const bulkUploadId = data.bulkUploadId;

      const newJobs: BulkDocumentJob[] = data.uploads.map((upload, i) => ({
        jobId: upload.jobId,
        filename: upload.fileName,
        fileSize: fileArray[i]?.size,
        documentId: upload.documentId,
        bulkUploadId,
        createdAt: Date.now(),
      }));

      onJobsAdded(newJobs);

      // Fire all S3 PUTs in parallel. Per-file failure is captured so the
      // matching job card can show "failed" without aborting the batch.
      await Promise.allSettled(
        data.uploads.map((upload, i) => {
          const file = fileArray[i]!;
          return uploadToS3({ url: upload.uploadUrl, file }).catch((err) => {
            console.error("[BulkImportPanel] S3 upload failed", {
              fileName: file.name,
              err,
            });
            onJobFailed(upload.jobId);
            throw err;
          });
        }),
      );

      // Backstop the workflow scheduling in case some S3 ObjectCreated
      // events didn't land (the per-doc /reprocess endpoint exists for
      // exactly this reason; this is its batch counterpart).
      reprocessBulk({ bulkUploadId });
    } catch (err) {
      console.error("[BulkImportPanel] Bulk upload failed", err);
      setError(
        err instanceof Error
          ? `Upload failed: ${err.message}`
          : "Upload failed. Please try again.",
      );
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    void handleFiles(e.dataTransfer.files);
  };

  return (
    <VerticalFlexbox gap="sm">
      <button
        type="button"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        id={uploadTriggerId}
        aria-label="Upload care-gap documents"
        aria-describedby="bulk-upload-hint"
        className={cn(
          "flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/30 px-4 py-8 text-center transition-all",
          "hover:border-accent/60 hover:bg-accent/5",
          isDragging && "border-accent bg-accent/10",
        )}
      >
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className="size-10 rounded-full bg-accent/10 text-accent"
        >
          <Upload className="size-5" />
        </Flexbox>
        <Text sizing="sm" fontWeight={500}>
          Drag &amp; drop care-gap PDFs here
        </Text>
        <Text sizing="xs" appearance="subdued" id="bulk-upload-hint">
          Up to 100 files per batch · PDF, PNG, JPG · 25 MB per file
        </Text>
        <Text
          as="span"
          sizing="xs"
          fontWeight={600}
          className="mt-1 rounded-md bg-background px-3 py-1 text-accent shadow-sm"
        >
          Browse files
        </Text>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          multiple
          className="hidden"
          aria-labelledby={uploadTriggerId}
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </button>

      {error ? (
        <Text sizing="xs" className="text-destructive" role="alert">
          {error}
        </Text>
      ) : null}

      {jobs.length > 0 ? (
        <VerticalFlexbox gap="xs" className="max-h-96 overflow-y-auto">
          {jobs.map((job) => (
            <BulkJobCard key={job.jobId} job={job} onRemove={onJobRemove} />
          ))}
        </VerticalFlexbox>
      ) : null}
    </VerticalFlexbox>
  );
};
